import {
  AudienceMatchType,
  AutomationNodeTypeType,
  AutomationWaitUnitType,
  type AutomationNodeInputType,
  type AutomationTriggerConfigInputType,
} from "@/types/graphql/partner.generated";
import type { AutomationEditorNode } from "@/partner/entities/marketing";
import { toFilterInput } from "./filterInput";
import type { AutomationNodeData, TriggerData } from "./graphTypes";

/**
 * Что кабинету нужно знать про тип узла, чтобы читать и писать его
 * конфиг: пустой конфиг нового шага, префикс id, чтение из ответа сервера
 * и запись во вход мутации. Правила типа (выходы, вход, удаление, предел
 * числа) здесь не живут: их отдаёт сервер (Automation.graphRules, см.
 * nodeRules.ts). Единственное место с именами типов узлов в модели:
 * fromAutomation, toAutomationInput и операции графа не сравнивают тип с
 * конкретным значением; шаги цепочки (WAIT, CONDITION) это такие же записи.
 */
export interface NodeTypeSpec {
  emptyData: () => Partial<Omit<AutomationNodeData, "type">>;
  idPrefix: string;
  /** Конфиг этого типа из узла, пришедшего с сервера. */
  fromServer: (
    node: AutomationEditorNode
  ) => Partial<Omit<AutomationNodeData, "type">>;
  /** Конфиг этого типа для входа мутации; поля вне этого набора в мутацию не попадают. */
  toInput: (
    data: AutomationNodeData
  ) => Pick<
    AutomationNodeInputType,
    "trigger" | "sendEmail" | "wait" | "condition"
  >;
}

/** Событие не выбрано, параметров нет: новый шаг «Событие». */
export const emptyTrigger = (): TriggerData => ({
  event: null,
  tagIds: [],
  productIds: [],
  contentIds: [],
  daysBefore: null,
  reasons: [],
});

/**
 * Вход события. Поля перечислены явно (I1): лишний ключ в объекте события
 * из UI в мутацию не уходит; порядок ключей один для снимка правок.
 * Параметры цепочки уходят, только если заданы: сервер читает
 * отсутствующий параметр как пустой, а вход автоматизации по событию
 * остаётся тем же, что в фазе 3 (event и tagIds), и кабинет можно выкатить
 * независимо от бэкенда.
 */
function triggerInput(trigger: TriggerData): AutomationTriggerConfigInputType {
  return {
    event: trigger.event,
    tagIds: trigger.tagIds,
    ...(trigger.productIds.length > 0
      ? { productIds: trigger.productIds }
      : {}),
    ...(trigger.contentIds.length > 0
      ? { contentIds: trigger.contentIds }
      : {}),
    ...(trigger.daysBefore !== null ? { daysBefore: trigger.daysBefore } : {}),
    ...(trigger.reasons.length > 0 ? { reasons: trigger.reasons } : {}),
  };
}

/**
 * Тип без записи в таблице: узел неизвестного кабинету типа (старый
 * кабинет против нового сервера). Его можно показать, но записать в него
 * настройки кабинет не умеет (см. unsupportedNodeIds).
 */
const UNKNOWN_NODE_SPEC: NodeTypeSpec = {
  emptyData: () => ({}),
  idPrefix: "step",
  fromServer: () => ({}),
  toInput: () => ({}),
};

export const NODE_TYPE_TABLE = new Map<string, NodeTypeSpec>([
  [
    AutomationNodeTypeType.TriggerType,
    {
      emptyData: () => ({ trigger: emptyTrigger() }),
      idPrefix: "trigger",
      fromServer: (node) => ({
        trigger: node.trigger
          ? {
              event: node.trigger.event ?? null,
              tagIds: [...node.trigger.tagIds],
              productIds: [...node.trigger.productIds],
              contentIds: [...node.trigger.contentIds],
              daysBefore: node.trigger.daysBefore ?? null,
              reasons: [...node.trigger.reasons],
            }
          : null,
      }),
      toInput: (data) => ({
        trigger: data.trigger ? triggerInput(data.trigger) : null,
      }),
    },
  ],
  [
    AutomationNodeTypeType.SendEmailType,
    {
      emptyData: () => ({ sendEmail: { emailTemplateId: null } }),
      idPrefix: "send",
      fromServer: (node) => ({
        sendEmail: node.sendEmail
          ? { emailTemplateId: node.sendEmail.emailTemplateId ?? null }
          : null,
      }),
      toInput: (data) => ({
        sendEmail: data.sendEmail
          ? { emailTemplateId: data.sendEmail.emailTemplateId }
          : null,
      }),
    },
  ],
  [
    AutomationNodeTypeType.ExitType,
    {
      emptyData: () => ({}),
      idPrefix: "exit",
      fromServer: () => ({}),
      toInput: () => ({}),
    },
  ],
  [
    AutomationNodeTypeType.WaitType,
    {
      // Новый шаг сразу виден осмысленным: один день.
      emptyData: () => ({
        wait: { value: 1, unit: AutomationWaitUnitType.DaysType },
      }),
      idPrefix: "wait",
      fromServer: (node) => ({
        wait: node.wait
          ? { value: node.wait.value, unit: node.wait.unit }
          : null,
      }),
      toInput: (data) => ({
        wait: data.wait
          ? { value: data.wait.value, unit: data.wait.unit }
          : null,
      }),
    },
  ],
  [
    AutomationNodeTypeType.ConditionType,
    {
      // Пустой фильтр: сервер покажет CONDITION_FILTER_REQUIRED, пока
      // партнёр не задаст условие.
      emptyData: () => ({
        condition: {
          filter: { match: AudienceMatchType.AllType, conditions: [] },
        },
      }),
      idPrefix: "cond",
      fromServer: (node) => ({
        condition: node.condition
          ? { filter: toFilterInput(node.condition.filter) }
          : null,
      }),
      // Канонический вид фильтра (как у аудитории): тот же фильтр в другой
      // форме объекта не считается правкой.
      toInput: (data) => ({
        condition: data.condition
          ? { filter: toFilterInput(data.condition.filter) }
          : null,
      }),
    },
  ],
]);

export const nodeTypeSpec = (type: string): NodeTypeSpec =>
  NODE_TYPE_TABLE.get(type) ?? UNKNOWN_NODE_SPEC;

export function emptyNodeData(
  type: AutomationNodeTypeType
): AutomationNodeData {
  return {
    type,
    trigger: null,
    sendEmail: null,
    wait: null,
    condition: null,
    ...nodeTypeSpec(type).emptyData(),
  };
}
